import type Stripe from 'stripe'

/**
 * Open a Stripe Connect account for the user and returns the account id.
 *
 * @returns A promise that resolves when the user's Stripe Account has been
 * created with the Stripe Connect id.
 */
export async function openStripeAccount() {
  const auth = useAuth()
  const toast = useToast()

  if (!auth.user?.stripe_connect_id) {
    const stripeAccount = await $fetch<{
      status_code: number
      message: string
      data: Stripe.Response<Stripe.Account>
      error?: undefined
    }>('/api/v1/stripe/account', {
      method: 'POST',
    })

    if (stripeAccount.status_code !== 200) {
      console.error(new Error('Could not create Stripe Connect account.'))

      toast.add({
        color: 'red',
        title: 'Could not create Stripe Connect account.',
        description: 'Please try again later.',
      })

      return
    }

    if (!stripeAccount.data?.id) {
      console.error('Stripe didn\'t return an account id.')

      toast.add({
        color: 'red',
        title: 'Could not create Stripe Connect account.',
        description: 'Please try again later.',
      })

      return
    }

    return stripeAccount.data
  }
}

/**
 * Get the Stripe Connect link for the current user.
 *
 * @returns A promise that resolves with the Stripe Connect link.
 */
export async function getStripeAccountLink() {
  const auth = useAuth()
  const toast = useToast()

  if (!auth.user) {
    throw new Error('User is not authenticated.')
  }

  if (typeof auth.user.stripe_connect_id !== 'string') {
    throw new TypeError('User does not have a Stripe Connect account.')
  }

  const data = await $fetch<{
    status_code: number
    message: string
    data: Stripe.Response<Stripe.AccountLink>
    error?: undefined
  }>('/api/v1/stripe/account/link', {
    query: { id: auth.user.stripe_connect_id },
  })

  if (data.status_code !== 200) {
    toast.add({
      color: 'red',
      title: 'Could not get Stripe Connect account.',
      description: 'Please try again later.',
    })

    throw new Error('Could not get Stripe Connect account.')
  }

  if (typeof data.data?.url === 'string') {
    return data.data.url
  }
  else {
    toast.add({
      color: 'red',
      title: 'Could not get Stripe Connect account.',
      description: 'Please try again later.',
    })

    throw new Error('No Stripe account link')
  }

  return ''
}

/**
 * Open the Stripe Connect link for the current user.
 */
export async function toStripeAccount() {
  const auth = useAuth()

  if (!auth.user) {
    throw new Error('User is not authenticated.')
  }

  if (typeof auth.user.stripe_connect_id === 'string') {
    const link = await getStripeAccountLink()

    if (link) {
      await navigateTo(link, { external: true })
    }
  }
  else {
    await openStripeAccount()

    const link = await getStripeAccountLink()

    if (link) {
      await navigateTo(link, { external: true })
    }
  }
}
